import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { GET } from '../../utils/httpMethods';
import './DashboardStats.scss';

const StatsPieChart = ({ data, height = 170, formatLabel }) => {
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }

        const updateWidth = ([entry]) => {
            setContainerWidth(entry.contentRect.width);
        };

        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const isCompactLegend = containerWidth > 0 && containerWidth < 250;
    const chartHeight = isCompactLegend ? height + 44 : height;
    const pieCenterX = isCompactLegend ? '50%' : '45%';
    const pieCenterY = isCompactLegend ? '40%' : '50%';
    const pieOuterRadius = isCompactLegend ? 36 : 40;

    const renderOuterLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
        const RADIAN = Math.PI / 180;
        const radius = outerRadius + 18;
        const x = cx + (radius * Math.cos(-midAngle * RADIAN));
        const y = cy + (radius * Math.sin(-midAngle * RADIAN));

        const item = data.find(d => d.name === name);

        return percent > 0.0001 ? (
            <text
                x={x}
                y={y}
                fill={item && item.color ? item.color : '#464646'}
                textAnchor={x > cx ? 'start' : 'end'}
                dominantBaseline="central"
                fontSize={12}
                fontWeight="bold"
            >
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        ) : null;
    };

    return (
        <div
            ref={containerRef}
            className={`dashboard-stats__chart ${isCompactLegend ? 'dashboard-stats__chart--legend-bottom' : ''}`}
        >
            <ResponsiveContainer width="100%" height={chartHeight}>
                <PieChart>
                    <Pie
                        data={data}
                        cx={pieCenterX}
                        cy={pieCenterY}
                        outerRadius={pieOuterRadius}
                        label={renderOuterLabel}
                        labelLine={false}
                        isAnimationActive={false}
                    >
                        {data.map((entry) => (
                            <Cell key={`cell-${entry.name}`} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip formatLabel={formatLabel} />} />
                    <Legend
                        layout={isCompactLegend ? 'horizontal' : 'vertical'}
                        verticalAlign={isCompactLegend ? 'bottom' : 'middle'}
                        align={isCompactLegend ? 'center' : 'right'}
                        iconType="circle"
                        iconSize={8}
                        formatter={(value) => formatLabel(value)}
                        wrapperStyle={{ fontSize: '11px', color: '#464646' }}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

StatsPieChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
        color: PropTypes.string,
    })).isRequired,
    height: PropTypes.number,
    formatLabel: PropTypes.func.isRequired,
};

StatsPieChart.defaultProps = {
    height: 170,
};

const CHART_COLORS = ['#248fdf', '#25afe3', '#28eaea', '#61bd4f', 'darkorange', '#9a9a9a'];
const STAT_COLORS = {
    total: '#248fdf',
    signed: '#25afe3',
    signedManual: '#248fdf',
    signedElectronic: '#28eaea',
    pending: 'darkorange',
    unsigned: '#21206b',
    outdated: '#ffe600',
    archived: '#9a9a9a',
};

const LABELS = {
    ongoing: 'En cours',
    completed: 'Terminés',
    cancelled: 'Annulés',
    draft: 'Brouillons',

    single: 'Célibataire',
    married: 'Marié(e)',
    partner: 'Pacsé(e)',
    cohabitation: 'Concubinage',
    divorced: 'Divorcé(e)',
    widower: 'Veuf(ve)',
    unknown: 'Non renseigné',

    '0%': 'Tranche 1 (TMI = 0)',
    '11%': 'Tranche 2 (TMI = 11%)',
    '30%': 'Tranche 3 (TMI = 30%)',
    '41%': 'Tranche 4 (TMI = 41%)',
    '45%': 'Tranche 5 (TMI = 45%)',
};

const CustomTooltip = ({ active, payload, formatLabel }) => {
    if (!active || !payload || 0 === payload.length) {
        return null;
    }

    const [{ name, color }] = payload;

    return (
        <div className="dashboard-stats__tooltip">
            <div className="dashboard-stats__tooltip-line">
                <div className="dashboard-stats__tooltip-title">
                    <span
                        className="dashboard-stats__metric-dot"
                        style={{ backgroundColor: color }}
                    />
                    <span>{formatLabel(name)}</span>
                </div>
            </div>
        </div>
    );
};

CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.shape({
        color: PropTypes.string,
        name: PropTypes.string,
        value: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
    })),
    formatLabel: PropTypes.func,
};

CustomTooltip.defaultProps = {
    active: false,
    payload: [],
};

const DashboardStats = ({ refreshKey = 0, mode }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [autoRefreshKey, setAutoRefreshKey] = useState(0);

    useEffect(() => {
        const refreshStats = () => {
            setAutoRefreshKey(currentValue => currentValue + 1);
        };

        const handleVisibilityChange = () => {
            if ('visible' === document.visibilityState) {
                refreshStats();
            }
        };

        window.addEventListener('focus', refreshStats);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', refreshStats);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        GET(`/api/rips/ripStats/${mode}/`)
            .then(async (response) => {
                const data = await response.json();
                setStats(data);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [refreshKey, autoRefreshKey, mode]);

    if (loading) {
        return (
            <div className="dashboard-stats dashboard-stats--empty">
                Chargement des statistiques...
            </div>
        );
    }

    if (!stats || 0 === stats.total) {
        return (
            <div className="dashboard-stats dashboard-stats--empty">
                Aucune donnée disponible
            </div>
        );
    }

    // Données pour le graphique circulaire principal (signature des RIPs)
    const pieData = [
        { name: 'Manuelle', value: stats.signedManual, color: STAT_COLORS.signedManual },
        { name: 'Électronique', value: stats.signedElectronic, color: STAT_COLORS.signedElectronic },
        { name: 'En attente', value: stats.pending, color: STAT_COLORS.pending },
        { name: 'Non signé', value: stats.unsigned, color: STAT_COLORS.unsigned },
    ].filter(item => item.value > 0);

    const tmiOrder = ['0%', '11%', '30%', '41%', '45%'];
    const tmiData = Object.entries(stats.byTmi || {})
        .sort(([a], [b]) => tmiOrder.indexOf(a) - tmiOrder.indexOf(b))
        .map(([key, value], index) => ({
            name: key,
            value,
            color: CHART_COLORS[index % CHART_COLORS.length],
        }));

    const metricItems = [
        { key: 'total', label: 'Total', value: stats.total, color: STAT_COLORS.total },
        { key: 'signed', label: 'Signés', value: stats.signed, color: STAT_COLORS.signed },
        { key: 'pending', label: 'Signatures en attente', value: stats.pending, color: STAT_COLORS.pending },
        { key: 'unsigned', label: 'Non signés', value: stats.unsigned, color: STAT_COLORS.unsigned },
        { key: 'outdated', label: 'À actualiser', value: stats.outdated, color: STAT_COLORS.outdated },
        { key: 'archived', label: 'Archivés', value: stats.archived, color: STAT_COLORS.archived },
    ];

    const formatLabel = (value) => (LABELS[value] || value);

    return (
        <div className="dashboard-stats">
            <h4 className="dashboard-stats__title">Statistiques des RIPs</h4>

            <div className="dashboard-stats__metrics-list">
                {metricItems.map(({ key, label, value, color }) => (
                    <div
                        key={key}
                        className={`dashboard-stats__metric-row ${'total' === key ? 'dashboard-stats__metric-row--total' : 'dashboard-stats__metric-row--secondary'}`}
                    >
                        <div className="dashboard-stats__metric-label-group">
                            <span className="dashboard-stats__metric-dot" style={{ backgroundColor: color }} />
                            <span className="dashboard-stats__metric-label">{label}</span>
                        </div>
                        <span className="dashboard-stats__metric-value" style={{ color }}>{value}</span>
                    </div>
                ))}
            </div>

            {pieData.length > 0 && (
                <div className="dashboard-stats__panel">
                    <h5 className="dashboard-stats__panel-title">Par signature</h5>
                    <StatsPieChart data={pieData} height={160} formatLabel={formatLabel} />
                </div>
            )}

            <div className="dashboard-stats__secondary-panels">
                <div className="dashboard-stats__secondary-panels">
                    {tmiData.length > 0 && (
                        <div className="dashboard-stats__panel dashboard-stats__panel--half">
                            <h5 className="dashboard-stats__panel-title">Répartition par TMI</h5>
                            <StatsPieChart data={tmiData} height={150} formatLabel={formatLabel} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

DashboardStats.propTypes = {
    refreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mode: PropTypes.string,
};

DashboardStats.defaultProps = {
    refreshKey: 0,
    mode: 'user',
};

export default DashboardStats;
